import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CustomPagination from 'components/common/pagination/pagination';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { useAppSelector } from 'store/hooks';
import { userService } from 'components/api/users';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import { formatDate } from 'utils/date';
import { useNavigate } from 'react-router-dom';

const UserTips: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { userDetail } = useAppSelector((state) => state.users);

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
  });

  const fetchTips = useCallback(
    async (type: 'sent' | 'received', page: number, limit: number) => {
      if (!userDetail?.id) return;
      setIsLoading(true);
      try {
        const response = await userService.getUserTips(userDetail.id, { type, page, limit });

        const docs = response?.docs || response?.data?.docs || [];
        const totalDocs = response?.totalDocs ?? response?.data?.totalDocs ?? 0;

        setData(docs);
        setTotal(totalDocs);
      } catch (error) {
        console.error('Failed to fetch tips:', error);
        setData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [userDetail?.id],
  );

  useEffect(() => {
    fetchTips(activeTab === 0 ? 'sent' : 'received', params.page, params.limit);
  }, [activeTab, params.page, params.limit, fetchTips]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setParams({ page: 1, limit: 10 }); // Reset pagination on tab change
  };

  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize: number) => {
    setParams((prev) => ({ ...prev, limit: newSize, page: 1 }));
  };

  const renderPagination = () => (
    <CustomPagination
      count={total}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', flex: 0.5 },
      {
        field: 'counterpartyUsername',
        headerName: activeTab === 0 ? 'Recipient' : 'Sender',
        flex: 1,
        renderCell: (params) => (
          <Box
            component="span"
            sx={{
              cursor: 'pointer',
              color: 'var(--accent-blue)',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => navigate(`/clients/${params.row.counterpartyUserId}`)}
          >
            {params.row.counterpartyUsername}
          </Box>
        ),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        flex: 1,
        renderCell: (params) => {
          const amount = params.row.amount || 0;
          const currency = params.row.currencyCode || 'USD';
          try {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency,
              useGrouping: false,
            }).format(amount);
          } catch {
            return `${currency} ${amount}`;
          }
        },
      },
      {
        field: 'createdAt',
        headerName: 'Date',
        flex: 1,
        renderCell: (params) => formatDate(params.row.createdAt),
      },
    ],
    [activeTab],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              color: 'var(--text-secondary)',
              '&.Mui-selected': { color: 'white' },
            },
            '& .MuiTabs-indicator': { backgroundColor: 'var(--accent-blue)' },
          }}
        >
          <Tab label="Sent" />
          <Tab label="Received" />
        </Tabs>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data}
          rowCount={total}
          getRowId={(row) => row.id}
          columns={columns}
          loading={isLoading}
          sx={commonGridStyles}
          paginationMode="server"
          hideFooter
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default UserTips;
